import { X, Layers } from "lucide-react";
import { useProjectFilter } from "@/lib/projectFilter";

/**
 * Displays chips for currently-selected active projects with an ✕ button
 * per chip to quickly remove one from the filter. Hidden when nothing is
 * selected (i.e. "All Projects" mode).
 */
export default function ProjectFilterChips({ className = "" }) {
  const { selectedProjects, selectedIds, toggleId, clearAll } = useProjectFilter();

  if (selectedIds.length === 0) return null;

  const label = (p) => {
    if (p.project_type === "variation_order") return `VO · ${p.variation_order_name || p.project_name}`;
    if (p.project_type === "working_order") return `WO · ${p.wo_number || p.project_name}`;
    if (p.project_type === "internal") return p.project_name;
    return p.project_name || "Untitled";
  };

  // Consistent order with the dropdown: Internal → New projects → VO → WO
  const typeOrder = { internal: 0, new_project: 1, variation_order: 2, working_order: 3 };
  const sortedChips = [...selectedProjects].sort((a, b) => {
    const oa = typeOrder[a.project_type] ?? 99;
    const ob = typeOrder[b.project_type] ?? 99;
    if (oa !== ob) return oa - ob;
    return (label(a) || "").localeCompare(label(b) || "");
  });

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="project-filter-chips">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Filter aktif:</span>
      {sortedChips.map((p) => (
        <span
          key={p.id}
          data-testid={`project-chip-${p.id}`}
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] border ${p.project_type === "variation_order" ? "bg-[#FF5A1F]/10 border-[#FF5A1F] text-[#FF5A1F]" : "bg-slate-100 border-slate-300 text-[#0B1D3A]"}`}
        >
          <span className="max-w-[220px] truncate font-medium">{label(p)}</span>
          <button
            type="button"
            data-testid={`project-chip-remove-${p.id}`}
            onClick={() => toggleId(p.id)}
            className="hover:text-rose-600 shrink-0 inline-flex items-center"
            title="Hapus dari filter"
          >
            <X size={11} />
          </button>
        </span>
      ))}
      {selectedIds.length > 1 && (
        <button
          type="button"
          data-testid="project-chips-clear"
          onClick={clearAll}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-rose-600 border border-slate-300 hover:border-rose-400"
        >
          <Layers size={10} /> Clear all
        </button>
      )}
    </div>
  );
}
